import {
	Button,
	Flex,
	Typography,
	BorderColors,
	BorderRadius,
	ForegroundColors,
	Select,
	Filter,
	FlexChild,
	Field,
} from "@abel-technologies-as/components";
import { PlusCircle, Delete } from "@/shared/icons";
import { FiltersProps } from "./Filters.types";
import { useTranslation } from "react-i18next";
import { FilterType } from "@/shared/enums/Filter.enum";
import { FilterConfig } from "@/shared/types";
import { useEffect, useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";

export const Filters = ({
	filters,
	filterValues,
	onFilterChange,
	addTranslation = true,
	onClearFilter,
	showMoreFilters = false,
	onClearAll,
	onMoreFilters,
}: FiltersProps) => {
	const hasActiveFilters = Object.keys(filterValues).length > 0;
	const { t } = useTranslation("common");
	const [localFilters, setLocalFilters] = useState<Record<string, string>>({});
	const [debouncedFilters] = useDebouncedValue(localFilters, 300);

	useEffect(() => {
		for (const debouncedFilter of Object.keys(debouncedFilters)) {
			// eslint-disable-next-line security/detect-object-injection
			onFilterChange(debouncedFilter, debouncedFilters[debouncedFilter]);
		}
	}, [debouncedFilters]);

	const renderFilters = (filter: FilterConfig) => {
		if (filter.type === FilterType.INPUT) {
			return (
				<Field
					onChange={(e) =>
						setLocalFilters((prev) => ({
							...prev,
							[filter.id]: e.target.value,
						}))
					}
					value={localFilters[filter.id]}
					placeholder={filter.placeholder ? t(`filter.${filter.placeholder}`) : t("enter_value")}
				/>
			);
		}
		const selectTitle = addTranslation ? t(`filter.${filter.name}`) : filter.name;
		return (
			<Select
				showSearchBar={false}
				title={`${t("select")} ${selectTitle}`}
				zIndex={10001}
				value={filterValues[filter.id] || ""}
				items={filter.items.map((filter) => ({
					...filter,
					label: addTranslation ? `${t("filter." + filter.label)}` : filter.label,
				}))}
				itemId={"id"}
				itemLabel={"label"}
				onChange={(value) => onFilterChange(filter.id, value as string)}
			/>
		);
	};

	const renderFilterValue = (filter: FilterConfig) => {
		if (!filterValues[filter.id]?.length) return "";

		const filterValue = filterValues[filter.id];

		if (filter.type === FilterType.INPUT) return filterValue;

		return addTranslation ? t("filter." + filterValue) : filterValue;
	};

	const handleClearAll = () => {
		onClearAll();
		setLocalFilters({});
	};

	const handleOnClearFilter = (filter: FilterConfig) => {
		onClearFilter(filter.id);

		if (filter.type === FilterType.INPUT) {
			setLocalFilters({});
		}
	};

	return (
		<Flex
			flexWrap={"wrap"}
			gap={"8px"}
			alignItems={"center"}
			mediaSm="justify-content: flex-start; align-items: flex-start;">
			{filters.map((filter) => (
				<FlexChild key={filter.id}>
					<Filter
						value={renderFilterValue(filter)}
						onClear={() => handleOnClearFilter(filter)}
						filterDesc={filter.description}
						filterName={filter.name}>
						{renderFilters(filter)}
					</Filter>
				</FlexChild>
			))}

			{showMoreFilters && (
				<Button
					borderSize={"1px"}
					boxShadow={"0px 1px 2px 0px rgba(16, 24, 40, 0.05)"}
					customStyles={{ padding: "4px 8px" }}
					onClick={onMoreFilters}
					borderRadius={`${BorderRadius.SM}px`}
					iconBefore={<PlusCircle width="14px" height="14px" />}
					borderColor={BorderColors.Primary}>
					<Typography
						margin="0 0 0 4px"
						color={ForegroundColors.Secondary}
						fontSize="12px"
						fontWeight={500}
						lineHeight="18px"
						fontFamily="Inter-SemiBold">
						{t("filter.more_filters")}
					</Typography>
				</Button>
			)}

			{hasActiveFilters && (
				<Button
					borderSize={"1px"}
					boxShadow={"0px 1px 2px 0px rgba(16, 24, 40, 0.05)"}
					customStyles={{ padding: "4px 8px" }}
					onClick={handleClearAll}
					borderRadius={`${BorderRadius.SM}px`}
					iconBefore={<Delete width="14" height="14" />}
					borderColor={BorderColors.Primary}>
					<Typography
						margin="0 0 0 4px"
						color={ForegroundColors.Secondary}
						fontSize="12px"
						fontWeight={500}
						lineHeight="18px"
						fontFamily="Inter-SemiBold">
						{t("filter.clear_all")}
					</Typography>
				</Button>
			)}
		</Flex>
	);
};
